import {computed, observable, toJS} from 'mobx'
import {
  each,
  isArray,
  keys,
  mergeWith,
  omitBy,
  pick,
  pickBy,
  startsWith,
  union,
  filter,
} from 'lodash'
import {
  CustomerInfo,
  PaymentConfig,
  PaymentMethod,
  PaymentType,
  PseudoRecurringPaymentMethods,
  SubscriptionInfo,
} from '@raisenow/epik'
import {IFormBase} from './IFormBase'
import {
  ValidationConstraint,
  ValidationConstraints,
  ValidationErrors,
} from '../Validator'
import {PaymentFormBase, PaymentFormBaseArgs} from './PaymentFormBase'
import {createPaymentFormData, PaymentFormData} from './PaymentForm'
import {
  translateEpikErrorMessages,
  translateLocalErrorMessages,
} from '../helpers'
import {WidgetFlag} from '../WidgetFlag'

///////////////////////////////////////////////////////////////////////////////

export type SubscriptionRenewFormData = PaymentFormData
export type SubscriptionRenewFormArgs = PaymentFormBaseArgs

///////////////////////////////////////////////////////////////////////////////

export class SubscriptionRenewForm
  extends PaymentFormBase
  implements IFormBase<SubscriptionRenewFormData> {
  @observable data: SubscriptionRenewFormData
  @observable dirtyFields: string[]
  @observable validationErrors: ValidationErrors

  async init(args: SubscriptionRenewFormArgs) {
    await super.init(args)
    this.reset()
  }

  reset() {
    this.data = createSubscriptionRenewFormData({
      payment_type: PaymentType.RECURRING,
    })
    this.dirtyFields = []
    this.validationErrors = null
    this.resetPayment()
  }

  @computed get validationConstraints(): ValidationConstraints {
    let {computedConfig} = this.api

    return computedConfig.paymentValidations
  }

  @computed get requiredFields(): string[] {
    let constraints = toJS(this.validationConstraints)

    return keys(
      pickBy<ValidationConstraint>(constraints, (v, k) => {
        let isRequired = !!v.required || !!v.accepted
        let hasFalsyCondition =
          !!v.if && !this.api.compiler.checkCondition({if: v.if})

        return isRequired && !hasFalsyCondition
      }),
    )
  }

  getPossiblePaymentMethods(): PaymentMethod[] {
    let paymentMethods = super.getPossiblePaymentMethods()

    //don't allow pseudo recurring here
    paymentMethods = filter(
      paymentMethods,
      (pm) => PseudoRecurringPaymentMethods.indexOf(pm) === -1,
    )

    return paymentMethods
  }

  updateData(data: SubscriptionInfo | CustomerInfo | {[key: string]: any}) {
    let fields = [
      'subscription_token',
      'stored_rnw_purpose_id',
      'stored_campaign_id',
      'stored_campaign_subid',
      'stored_rnw_recurring_interval_name',

      'stored_customer_salutation',
      'stored_customer_firstname',
      'stored_customer_lastname',
      'stored_customer_email',
      'stored_customer_birthdate',
      'stored_customer_email_permission',
      'stored_customer_message',
      'stored_customer_donation_receipt',
      'stored_customer_street',
      'stored_customer_street_number',
      'stored_customer_street2',
      'stored_customer_pobox',
      'stored_customer_zip_code',
      'stored_customer_city',
      'stored_customer_country',
      'stored_customer_state',
    ]

    each(fields, (field) => {
      if (data[field] !== undefined) {
        this.data[field] = data[field]
      }
    })
  }

  /////////////////////////////////////////////////////////////////////////////
  //
  // PAYMENT
  //
  /////////////////////////////////////////////////////////////////////////////

  getPaymentConfig(): PaymentConfig {
    let {computedConfig} = this.api
    computedConfig = toJS(computedConfig)
    let formData = toJS(this.data)
    let customData = pickBy(formData, (v, k) => startsWith(k, 'stored_'))

    let data = {
      ...customData,

      subscription_token: formData['subscription_token'],

      return_parameters: false,
      immediate_execution: false,
      test_mode: computedConfig.testMode,
      language: computedConfig.language,
      further_rnw_interaction: false,
      flow: computedConfig.flow,

      payment_method: this.getPaymentMethod(),
      payment_provider: computedConfig.paymentProvider,
      recurring: true,
      recurring_interval: formData['stored_rnw_recurring_interval_name'],
      stored_rnw_recurring_interval_name:
        formData['stored_rnw_recurring_interval_name'],

      cardno: formData.cardno,
      cvv: formData.cvv,
      expm: formData.expm,
      expy: formData.expy,

      iban: formData.iban,
      bank_name: formData.bank_name,
      bank_zip_code: formData.bank_zip_code,
      bank_city: formData.bank_city,
      bankiban: formData.bankiban,
      bankbic: formData.bankbic,
      payer_line1: formData.payer_line1,
      payer_line2: formData.payer_line2,
      payer_line3: formData.payer_line3,
      payer_line4: formData.payer_line4,
      msisdn: formData.msisdn,

      stored_rnw_product_name: process.env.STORED_RNW_PRODUCT_NAME,
      stored_rnw_product_version: process.env.STORED_RNW_PRODUCT_VERSION,
      stored_rnw_widget_uuid: process.env.STORED_RNW_WIDGET_UUID,
      stored_rnw_source_url: window.location.href,
    } as Partial<PaymentConfig>

    data = omitBy(data, (v, k) => v === null || v === '' || v === undefined)

    return data as PaymentConfig
  }

  /////////////////////////////////////////////////////////////////////////////
  //
  // VALIDATE AND SEND
  //
  /////////////////////////////////////////////////////////////////////////////

  async validate(onlyDirty: boolean = false): Promise<ValidationErrors> {
    let {computedConfig, validator, trans} = this.api
    let payment = this.getPayment()
    let localErrors = await validator.validate(
      this.data,
      this.validationConstraints,
      this,
    )
    let epikErrors = await payment.validate()

    localErrors = translateLocalErrorMessages(
      localErrors,
      trans.bind(this.api),
      'validation_errors',
    )
    epikErrors = translateEpikErrorMessages(epikErrors, trans.bind(this.api))

    let allErrors = mergeWith({}, localErrors, epikErrors, (d1, d2) => {
      if (isArray(d1)) {
        return d1.concat(d2)
      }
    })

    let errors = allErrors

    if (onlyDirty) {
      errors = pick(errors, toJS(this.dirtyFields))
    }

    // this fields must not be validated
    delete errors.amount
    delete errors.currency

    // omit all errors for fields with "stored_" prefix
    errors = omitBy(errors, (v, k) => startsWith(k, 'stored_'))

    // omit hidden field errors
    errors = this.omitHiddenFieldErrors(errors)

    computedConfig.debug &&
      console.log('[tamaro] subscriptionRenewForm validation errors', {
        allErrors,
        errors,
      })

    errors = keys(errors).length > 0 ? errors : null
    this.validationErrors = errors
    this.dirtyFields = union(toJS(this.dirtyFields), keys(errors))

    return toJS(this.validationErrors)
  }

  async send(): Promise<void> {
    let {flags} = this.api
    let payment = this.getPayment()

    flags.add(WidgetFlag.SHOW_SPINNER_OUTER)

    try {
      await payment.send()
    } catch (error) {
      flags.remove(WidgetFlag.SHOW_SPINNER_OUTER)
    }
  }

  async validateAndSend(): Promise<void> {
    let {ui} = this.api
    let errors = await this.validate(false)

    if (errors) {
      await ui.scrollToTopInvalidField()
      await ui.focusTopInvalidField()

      return
    }

    await this.send()
  }
}

///////////////////////////////////////////////////////////////////////////////

export function createSubscriptionRenewForm(): SubscriptionRenewForm {
  return new SubscriptionRenewForm()
}

export const createSubscriptionRenewFormData = createPaymentFormData
